import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import useApi from "../../../apiProvider";
import toast from "react-hot-toast";
import { sortCircuits } from "../utils";
import { emptyCircuit } from "../Constants/emptyCircuit";
import { filterKeys } from "../Constants/filterKeys";
import { buildCircuitSavePayload } from "../utils/buildCircuitSavePayload";
import { buildSiteSavePayload } from "../utils/buildSiteSavePayload";

const SiteContext = createContext();

export const SSCProvider = ({ children }) => {
  const [formData, setFormData] = useState([]);
  const [allSites, setAllSites] = useState([]);
  const [allStructures, setAllStructures] = useState([]);
  const [allCircuits, setAllCircuits] = useState([]);
  const [diversityGroups, setDiversityGroups] = useState([]);
  const [diversityAssignments, setDiversityAssignments] = useState([]);

  const [selectedCarrierInfo, setSelectedCarrierInfo] = useState(null);
  const [carrierContacts, setCarrierContacts] = useState([]);

  const [globalSitesData, setGlobalSitesData] = useState([]);

  const [selectedSite, setSelectedSite] = useState(null);
  const [adoptiveSite, setAdoptiveSite] = useState(null);
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [selectedCircuit, setSelectedCircuit] = useState(null);
  const [originalSite, setOriginalSite] = useState(null);

  const [circuitUpdates, setCircuitUpdates] = useState({});

  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [filtering, setFiltering] = useState(false);
  const [filterValue, setFilterValue] = useState("");
  const [validationError, setValidationError] = useState("");
  const [requiredFields, setRequiredFields] = useState({});
  const [selectedEmptyStructure, setSelectedEmptyStructure] = useState(null);
  const [showEmptyStructures, setShowEmptyStructures] = useState(false);
  const [includeEmpty, setIncludeEmpty] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({
    Status: [],
    Use: [],
    Type: [],
    Category: [],
    Scope: [],
    Carrier: [],
    LEC: [],
    HandoffMedia: [],
    RedundancyType: [],
  });

  const getFilteredCircuits = useCallback(
    (thisStructure) => {
      const filtered = thisStructure?.circuits?.filter((circuit) => {
        const isNew = circuit.isNew === true;

        const statusMatch =
          selectedOptions["Status"].length === 0 ||
          selectedOptions["Status"].includes(circuit.status) ||
          (circuit.status == null && isNew);

        const carrierMatch =
          selectedOptions["Carrier"].length === 0 ||
          selectedOptions["Carrier"].includes(circuit.carrier) ||
          (circuit.carrier == null && isNew);

        const handoffTypeMatch =
          selectedOptions["HandoffMedia"].length === 0 ||
          selectedOptions["HandoffMedia"].includes(circuit.handoffType) ||
          (circuit.handoffType == null && isNew);

        const redundancyTypeMatch =
          selectedOptions["RedundancyType"].length === 0 ||
          selectedOptions["RedundancyType"].includes(circuit.redundancyType) ||
          (circuit.redundancyType == null && isNew);

        const lecMatch =
          selectedOptions["LEC"].length === 0 ||
          selectedOptions["LEC"].includes(circuit.lec) ||
          (circuit.lec == null && isNew);

        const scopeMatch =
          selectedOptions["Scope"].length === 0 ||
          selectedOptions["Scope"].includes(circuit.scope) ||
          (circuit.scope == null && isNew);

        const categoryMatch =
          selectedOptions["Category"].length === 0 ||
          selectedOptions["Category"].includes(circuit.category) ||
          (circuit.category == null && isNew);

        const typeMatch =
          selectedOptions["Type"].length === 0 ||
          selectedOptions["Type"].includes(circuit.type) ||
          (circuit.type == null && isNew);

        const useMatch =
          selectedOptions["Use"].length === 0 ||
          selectedOptions["Use"].includes(circuit.use) ||
          (circuit.use == null && isNew);

        const searchMatch =
          !filterValue ||
          circuit.circuitVendorId
            ?.toLowerCase()
            .includes(filterValue.toLowerCase()) ||
          circuit.circuitVendorId?.toString().includes(filterValue) ||
          circuit.carrier?.toLowerCase().includes(filterValue.toLowerCase()) ||
          circuit.lec?.toLowerCase().includes(filterValue.toLowerCase());

        return (
          statusMatch &&
          carrierMatch &&
          handoffTypeMatch &&
          redundancyTypeMatch &&
          lecMatch &&
          scopeMatch &&
          categoryMatch &&
          typeMatch &&
          useMatch &&
          searchMatch
        );
      });

      return sortCircuits(filtered);
    },
    [selectedOptions, filterValue],
  );

  const fetchSites = useCallback(async () => {
    setIsLoading(true);
    try {
      const [
        sites,
        structures,
        circuits,
        diversityGroups,
        diversityAssignments,
        formData,
      ] = await Promise.all([
        useApi.getSitesOnly(),
        useApi.getStructuresOnly(),
        useApi.getCircuitsOnly(),
        useApi.getDiversityGroupsOnly(),
        useApi.getDiversityGroupAssignmentsOnly(),
        useApi.getFormData(),
      ]);

      toast.success("Site Data Collected");

      setAllSites(sites);
      setAllStructures(structures);
      setAllCircuits(circuits);
      setDiversityGroups(diversityGroups);
      setDiversityAssignments(diversityAssignments);
      setFormData(formData);

      buildHierarchy(
        sites,
        structures,
        circuits,
        diversityGroups,
        diversityAssignments,
      );
    } catch (err) {
      console.error("Error fetching sites:", err);
      toast.error("Error fetching data");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchSite = async (siteId) => {
    setIsLoading(true);
    try {
      const site = await useApi.getSiteOnlyById(siteId);
      const siteModel = {
        ...site.siteData,
        structures: [],
      };

      const structureMap = {};
      site.structureData.forEach((structure) => {
        const structureWithCircuits = {
          ...structure,
          circuits: [],
        };

        siteModel.structures.push(structureWithCircuits);
        structureMap[structure.structureId] = structureWithCircuits;
      });

      const circuitMap = {};
      site.circuitData.forEach((circuit) => {
        const structure = structureMap[circuit.structureId];
        if (!structure) return;

        const circuitWithGroups = {
          ...circuit,
          diversityGroupIds: [],
        };

        structure.circuits.push(circuitWithGroups);
        circuitMap[circuit.circuitGmId] = circuitWithGroups;
      });

      toast.success("All Data site: " + siteId);
      return siteModel;
    } catch (err) {
      console.error("Error fetching sites:", err);
      toast.error("Error fetching data");
    }
  };

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  const handleFilter = (event) => {
    const value = event.target.value;
    setFilterValue(value);
  };

  const handleFilteringSelect = () => {
    if (filtering) {
      setFilterValue("");
      setSelectedOptions({
        Status: [],
        Use: [],
        Type: [],
        Category: [],
        Scope: [],
        Carrier: [],
        LEC: [],
        HandoffMedia: [],
        RedundancyType: [],
      });
    }
    setFiltering((prev) => !prev);
  };

  const buildHierarchy = (
    sites,
    structures,
    circuits,
    diversityGroups,
    diversityAssignments,
  ) => {
    const siteMap = {};

    sites.forEach((site) => {
      siteMap[site.siteId] = {
        ...site,
        structures: [],
        diversityGroups: [],
      };
    });

    diversityGroups.forEach((group) => {
      const site = siteMap[group.siteId];
      if (!site) return;

      site.diversityGroups.push({
        ...group,
        circuits: [],
        circuitCount: 0,
      });
    });

    const structureMap = {};
    structures.forEach((structure) => {
      const site = siteMap[structure.siteId];
      if (!site) return;

      const structureWithCircuits = {
        ...structure,
        circuits: [],
      };

      site.structures.push(structureWithCircuits);
      structureMap[structure.structureId] = structureWithCircuits;
    });

    const circuitMap = {};
    circuits.forEach((circuit) => {
      const structure = structureMap[circuit.structureId];
      if (!structure) return;

      const circuitWithGroups = {
        ...circuit,
        diversityGroupIds: [],
      };

      structure.circuits.push(circuitWithGroups);
      circuitMap[circuit.circuitGmId] = circuitWithGroups;
    });

    diversityAssignments.forEach((assignment) => {
      const circuit = circuitMap[assignment.circuitId];
      if (!circuit) return;

      const site = Object.values(siteMap).find((s) =>
        s.diversityGroups.some((g) => g.id === assignment.diversityGroupId),
      );
      if (!site) return;

      const siteGroup = site.diversityGroups.find(
        (g) => g.id === assignment.diversityGroupId,
      );
      if (!siteGroup) return;

      siteGroup.circuits.push(circuit); // attach full circuit object
      circuit.diversityGroupIds.push(siteGroup.id);
    });

    Object.values(siteMap).forEach((site) => {
      site.diversityGroups.forEach((group) => {
        group.circuitCount = group.circuits.length;
      });
    });

    setGlobalSitesData(Object.values(siteMap));

    return Object.values(siteMap);
  };

  const rebuildHierarchy = (options = {}) => {
    const {
      sites: updatedSites,
      structures: updatedStructures,
      circuits: updatedCircuits,
      groups: updatedGroups,
      assignments: updatedAssignments,
    } = options;

    const sitesToUse = updatedSites ?? allSites;
    const structuresToUse = updatedStructures ?? allStructures;
    const circuitsToUse = updatedCircuits ?? allCircuits;
    const groupsToUse = updatedGroups ?? diversityGroups;
    const assignmentsToUse = updatedAssignments ?? diversityAssignments;

    const sitesHierarchy = buildHierarchy(
      sitesToUse,
      structuresToUse,
      circuitsToUse,
      groupsToUse,
      assignmentsToUse,
    );

    if (updatedSites) setAllSites(updatedSites);
    if (updatedStructures) setAllStructures(updatedStructures);
    if (updatedCircuits) setAllCircuits(updatedCircuits);
    if (updatedGroups) setDiversityGroups(updatedGroups);
    if (updatedAssignments) setDiversityAssignments(updatedAssignments);

    const sitesFromHierarchy = sitesHierarchy.find(
      (s) => s.siteId === selectedSite?.siteId,
    );
    if (sitesFromHierarchy) {
      const siteSnapshot = JSON.parse(JSON.stringify(sitesFromHierarchy));
      setSelectedSite(siteSnapshot);
      setOriginalSite(siteSnapshot);
    }
    closeEmptyStrucutres();
    setCircuitUpdates({});
    return sitesHierarchy;
  };

  const filterData = useCallback(
    (data, fields) => {
      if (!inputValue || inputValue.length < 3) return [];
      const searchValue = inputValue.toLowerCase().trim();
      const searchTerms = searchValue.split(/\s+/);

      return data.filter((item) =>
        searchTerms.every((term) =>
          fields.some((field) =>
            String(item[field] || "")
              .toLowerCase()
              .includes(term),
          ),
        ),
      );
    },
    [inputValue],
  );

  const structureIdToSiteId = useMemo(() => {
    const map = new Map();
    for (const s of allStructures) {
      if (s?.structureId != null && s?.siteId != null) {
        map.set(s.structureId, s.siteId);
      }
    }
    return map;
  }, [allStructures]);

  const structureIdsWithCircuits = useMemo(() => {
    const set = new Set();
    for (const c of allCircuits) {
      if (c?.structureId != null) set.add(c.structureId);
    }
    return set;
  }, [allCircuits]);

  const siteIdsWithCircuits = useMemo(() => {
    const set = new Set();
    for (const structureId of structureIdsWithCircuits) {
      const siteId = structureIdToSiteId.get(structureId);
      if (siteId != null) set.add(siteId);
    }
    return set;
  }, [structureIdsWithCircuits, structureIdToSiteId]);

  const filteredSites = useMemo(() => {
    const searched = filterData(
      allSites.filter((s) => s.siteId < 90000),
      filterKeys.site,
    );

    if (!includeEmpty) {
      return searched.filter((s) => siteIdsWithCircuits.has(s.siteId));
    }

    return searched;
  }, [allSites, includeEmpty, siteIdsWithCircuits, filterData]);

  const filteredTempSites = useMemo(() => {
    const searched = filterData(
      allSites.filter((s) => s.siteId >= 90000),
      filterKeys.site,
    );

    if (!includeEmpty) {
      return searched.filter((s) => siteIdsWithCircuits.has(s.siteId));
    }

    return searched;
  }, [allSites, includeEmpty, siteIdsWithCircuits, filterData]);

  const filteredStructures = useMemo(() => {
    const searched = filterData(
      allStructures.filter((s) => s.structureId < 90000),
      filterKeys.structure,
    );

    if (!includeEmpty) {
      return searched.filter((s) =>
        structureIdsWithCircuits.has(s.structureId),
      );
    }

    return searched;
  }, [allStructures, includeEmpty, structureIdsWithCircuits, filterData]);

  const filteredTempStructures = useMemo(() => {
    const searched = filterData(
      allStructures.filter((s) => s.structureId >= 90000),
      filterKeys.structure,
    );

    if (!includeEmpty) {
      return searched.filter((s) =>
        structureIdsWithCircuits.has(s.structureId),
      );
    }

    return searched;
  }, [allStructures, includeEmpty, structureIdsWithCircuits, filterData]);

  const filteredCircuits = useMemo(
    () => filterData(allCircuits, filterKeys.circuit),
    [allCircuits, filterData],
  );

  const displaySites =
    inputValue && inputValue.length >= 3 ? filteredSites : globalSitesData;

  const handleSelectCircuit = (circuitGmId) => {
    const circuit = allCircuits.find((c) => c.circuitGmId === circuitGmId);
    if (!circuit) return;

    if (!handleSelectStructure(circuit.structureId)) return;

    setSelectedCircuit(circuit);
  };

  const handleSelectStructure = (structureId) => {
    const structure = allStructures.find((s) => s.structureId === structureId);
    if (!structure) return false;

    if (!handleSelectSite(structure.siteId, false)) return false;

    setSelectedStructure(structure);
    setSelectedCircuit(null);

    return true;
  };

  const handleSelectSite = (siteId, clearDependents = true) => {
    const site = globalSitesData.find((s) => s.siteId === siteId);
    if (!site) return false;
    closeEmptyStrucutres();
    if (clearDependents) {
      setSelectedCircuit(null);
      setSelectedStructure(null);
    }

    const deepCopy = JSON.parse(JSON.stringify(site));
    setSelectedSite(deepCopy);
    setOriginalSite(JSON.parse(JSON.stringify(deepCopy)));
    setCircuitUpdates({});
    setInputValue(
      `${deepCopy.siteId} - ${deepCopy.siteName} - ${deepCopy.city}`,
    );
    setValidationError("");
    setIsDropdownVisible(false);

    return true;
  };

  const closeEmptyStrucutres = () => {
    setSelectedEmptyStructure(null);
    setShowEmptyStructures(false);
  };

  const handleCircuitChange = (structureId, circuitId, updatedFields) => {
    setCircuitUpdates((prev) => {
      const updated = { ...prev };

      // If no changes, remove the circuit entry
      if (Object.keys(updatedFields).length === 0) {
        if (updated[structureId]) {
          delete updated[structureId][circuitId];
          if (Object.keys(updated[structureId]).length === 0) {
            delete updated[structureId];
          }
        }
        return updated;
      }

      updated[structureId] ??= {};
      updated[structureId][circuitId] = updatedFields;

      return updated;
    });
  };

  const resetSite = () => {
    if (!originalSite) return;
    setSelectedSite(JSON.parse(JSON.stringify(originalSite)));
    setCircuitUpdates({});
    setSelectedEmptyStructure(null);
    setShowEmptyStructures(false);
  };

  const validateRequiredCircuits = () => {
    const missing = {};

    selectedSite.structures.forEach((structure) => {
      structure.circuits
        .filter((circuit) => {
          if (circuit.isNew) return true;

          return Boolean(
            circuitUpdates?.[structure.structureId]?.[circuit.circuitGmId],
          );
        })
        .forEach((circuit) => {
          const updatesForCircuit =
            circuitUpdates?.[structure.structureId]?.[circuit.circuitGmId] ||
            {};

          const mergedCircuit = { ...circuit, ...updatesForCircuit };

          const missingFields = [];
          if (!mergedCircuit.carrier) missingFields.push("carrier");
          if (!mergedCircuit.lec) missingFields.push("lec");
          if (!mergedCircuit.status) missingFields.push("status");
          if (!mergedCircuit.circuitVendorId)
            missingFields.push("circuitVendorId");

          if (missingFields.length > 0) {
            missing[structure.structureId] ??= {};
            missing[structure.structureId][
              circuit.circuitGmId ?? circuit.tempId
            ] = missingFields;
          }
        });
    });

    if (Object.keys(missing).length > 0) {
      setRequiredFields(missing);
      setIsLoading(false);
      setValidationError("Please fill out all required fields before saving.");
      return false;
    }

    setValidationError("");
    return true;
  };

  const buildSavePayload = () => {
    if (!selectedSite || !hasChanges) return null;

    if (!validateRequiredCircuits()) {
      return;
    }

    const structures = Object.entries(circuitUpdates).map(
      ([structureId, circuitsById]) => {
        const updatedCircuits = Object.entries(circuitsById).map(
          ([circuitId, fields]) => ({
            circuitId,
            fields,
          }),
        );

        const structure = selectedSite.structures.find(
          (s) => String(s.structureId) === String(structureId),
        );

        if (!structure) return null;

        return {
          ...structure,
          circuits: buildCircuitSavePayload(
            allCircuits,
            updatedCircuits,
            formData,
            selectedSite.diversityGroups,
            structureId,
          ),
        };
      },
    );

    const sanitizedSite = buildSiteSavePayload(selectedSite);

    return {
      ...sanitizedSite,
      cafmStructureData: structures ?? [],
    };
  };

  const removeTempCircuit = (structureId, circuitId) => {
    if (!structureId) return;
    setSelectedSite((prevSite) => {
      if (!prevSite || !Array.isArray(prevSite.structures)) {
        return prevSite;
      }
      return {
        ...prevSite,
        structures: prevSite.structures.map((structure) => {
          if (structure.structureId !== structureId) return structure;

          return {
            ...structure,
            circuits: structure.circuits
              ? structure.circuits.filter((circuit) =>
                  circuitId != null
                    ? circuit.circuitGmId !== circuitId
                    : !circuit.isNew,
                )
              : [],
          };
        }),
      };
    });
    if (selectedEmptyStructure?.structureId === structureId) {
      setSelectedEmptyStructure(null);
    }
  };

  const handleHideEmptyStructures = () => {
    if (selectedEmptyStructure?.structureId) {
      removeTempCircuit(selectedEmptyStructure.structureId, null);
    }
    setShowEmptyStructures(false);
  };

  const handleTempSiteSelect = (newTempSite, newTempStructure) => {
    const structureId = newTempStructure.structureId;
    setOriginalSite({
      ...newTempSite,
      structures: [
        {
          ...newTempStructure,
          circuits: [],
        },
      ],
    });

    setSelectedSite({
      ...newTempSite,
      structures: [
        {
          ...newTempStructure,
          circuits: [
            {
              ...emptyCircuit,
              circuitGmId: Date.now().toString(),
              structureId,
              isNew: true,
            },
          ],
        },
      ],
    });

    setInputValue(
      `${newTempSite.siteId} - ${newTempSite.siteName} - ${newTempSite.city}`,
    );

    if (newTempStructure) {
      setSelectedEmptyStructure({
        ...newTempStructure,
        circuits: [],
      });
      setShowEmptyStructures(true);
    }
  };

  const hasChanges = useMemo(() => {
    if (!selectedSite) return false;

    if (!originalSite) return true;

    const siteNoteChanged =
      (originalSite.note ?? "") !== (selectedSite.note ?? "");

    const circuitsChanged = Object.keys(circuitUpdates).length > 0;

    const hasNewCircuits = selectedSite.structures?.some((s) =>
      s.circuits?.some((c) => c.isNew),
    );

    return siteNoteChanged || circuitsChanged || hasNewCircuits;
  }, [originalSite, selectedSite, circuitUpdates]);

  const addCircuit = (structureId) => {
    setSelectedSite((prevSite) => ({
      ...prevSite,
      structures: prevSite.structures.map((structure) =>
        structure.structureId === structureId
          ? {
              ...structure,
              circuits: [
                {
                  ...emptyCircuit,
                  circuitGmId: Date.now().toString(),
                  structureId,
                },
                ...structure?.circuits,
              ],
            }
          : structure,
      ),
    }));
  };

  const selectEmptyStructure = (structureId, structureToUpsert = null) => {
    setSelectedSite((prevSite) => {
      if (!prevSite) return prevSite;

      const sid = String(structureId);
      const prevStructures = prevSite.structures ?? [];

      let baseStructures = prevStructures;

      if (structureToUpsert) {
        const upsertId = String(structureToUpsert.structureId);

        const exists = prevStructures.some(
          (s) => String(s.structureId) === upsertId,
        );

        baseStructures = exists
          ? prevStructures.map((s) =>
              String(s.structureId) === upsertId
                ? { ...s, ...structureToUpsert, circuits: s.circuits ?? [] } // keep circuits
                : s,
            )
          : [...prevStructures, { ...structureToUpsert, circuits: [] }];
      }

      let selectedStructure = null;

      const updatedStructures = baseStructures.map((s) => {
        const structure = { ...s, circuits: s.circuits ?? [] };

        if (String(structure.structureId) !== sid) return structure;

        const alreadyHasNew = structure.circuits.some((c) => c.isNew);
        const newCircuits = alreadyHasNew
          ? structure.circuits
          : [
              {
                ...emptyCircuit,
                circuitGmId: Date.now().toString(),
                structureId,
                isNew: true,
              },
              ...structure.circuits,
            ];

        selectedStructure = { ...structure, circuits: newCircuits };
        return selectedStructure;
      });

      if (selectedStructure) {
        setSelectedEmptyStructure(selectedStructure);
      }

      return {
        ...prevSite,
        structures: updatedStructures,
      };
    });
  };

  const handleCarrierInfoClick = async (carrier) => {
    if (carrier !== "") {
      let contacts = await useApi.getContactsByCarrierName(carrier);
      if (contacts === undefined || contacts.length === 0) {
        setSelectedCarrierInfo(null);
        setCarrierContacts([]);
        toast.error("No contacts for given carrier");
        return;
      }
      setSelectedCarrierInfo(carrier);
      setCarrierContacts(contacts);
    } else {
      toast.error("No carrier for given circuit");
    }
  };

  const sortedCarrierContacts = useMemo(() => {
    const defaultSort = [
      "Support",
      "Escalation",
      "Account",
      "Billing",
      "Admin",
    ];

    return [...(carrierContacts ?? [])].sort((a, b) => {
      const aIndex = defaultSort.indexOf(a.role);
      const bIndex = defaultSort.indexOf(b.role);

      const aPriority = aIndex === -1 ? Infinity : aIndex;
      const bPriority = bIndex === -1 ? Infinity : bIndex;

      if (aPriority !== bPriority) return aPriority - bPriority;

      const aName = a.name?.toLowerCase() || "";
      const bName = b.name?.toLowerCase() || "";
      return aName.localeCompare(bName);
    });
  }, [carrierContacts]);

  const handleAdoptiveSite = (adoptiveSiteId) => {
    setAdoptiveSite(globalSitesData.find((s) => s.siteId === adoptiveSiteId));
  };

  useEffect(() => {
    if (!hasChanges) {
      setValidationError("");
    }
  }, [hasChanges]);

  return (
    <SiteContext.Provider
      value={{
        isLoading,
        setIsLoading,
        selectedSite,
        originalSite,
        circuitUpdates,
        handleCircuitChange,
        resetSite,
        filteredSites,
        filteredStructures,
        filteredCircuits,
        globalSitesData,
        inputValue,
        setInputValue,
        isDropdownVisible,
        setIsDropdownVisible,
        displaySites,
        handleSelectSite,
        formData,
        setSelectedSite,
        filtering,
        selectedOptions,
        setSelectedOptions,
        getFilteredCircuits,
        hasChanges,
        diversityGroups,
        rebuildHierarchy,
        addCircuit,
        selectEmptyStructure,
        fetchSite,
        handleCarrierInfoClick,
        sortedCarrierContacts,
        carrierContacts,
        setCarrierContacts,
        selectedCarrierInfo,
        setSelectedCarrierInfo,
        buildSavePayload,
        validationError,
        validateRequiredCircuits,
        requiredFields,
        filteredTempSites,
        filteredTempStructures,
        handleFilter,
        handleFilteringSelect,
        removeTempCircuit,
        showEmptyStructures,
        setShowEmptyStructures,
        handleHideEmptyStructures,
        handleTempSiteSelect,
        selectedEmptyStructure,
        includeEmpty,
        setIncludeEmpty,
        selectedStructure,
        selectedCircuit,
        handleSelectStructure,
        handleSelectCircuit,
        allSites,
        handleAdoptiveSite,
        setAdoptiveSite,
        adoptiveSite,
      }}
    >
      {children}
    </SiteContext.Provider>
  );
};

export const useSiteContext = () => {
  const context = useContext(SiteContext);
  if (!context)
    throw new Error("useSiteContext must be used within SSCProvider");
  return context;
};
